import React, {useEffect, useRef, useState} from 'react';
import {
    SideNavigationSearchBar,
    SideNavigationSearchBarProps,
} from './side-navigation-search-bar/SideNavigationSearchBar';
import {SideNavigationSearchCollapsed} from './side-navigation-search-collapsed/SideNavigationSearchCollapsed';
import {useSideNavigationCollapseContext} from '../side-navigation-contexts/SideNavigationCollapseContext';
import {useSearchValue} from '../side-navigation-contexts/SideNavigationSearchContext';
import {SideNavigationMenuItemType} from '../types/SideNavigationTypes';

export interface SideNavigationSearchProps {
    /** The text to be displayed on the search bar before any input is given. */
    placeholder?: string;
    /** The emitted value when a search input is given. */
    onChange: (filteredMenuItems: [SideNavigationMenuItemType[], SideNavigationMenuItemType[]]) => void;
    primaryMenuItems: SideNavigationMenuItemType[];
    secondaryMenuItems: SideNavigationMenuItemType[];
}

export const SideNavigationSearch = ({
                                         placeholder = 'Search',
                                         onChange,
                                         primaryMenuItems,
                                         secondaryMenuItems
                                     }: SideNavigationSearchProps) => {

    const inputRef = useRef<HTMLInputElement | null>(null);
    const {searchValue, setSearchValue} = useSearchValue();
    const {isCollapsed} = useSideNavigationCollapseContext();
    const [isInitialRender, setIsInitialRender] = useState<boolean>(true);
    const [isFocused, setIsFocused] = useState<boolean>(false);

    const handleFocusingSearch = () => {
        if (inputRef.current && !isFocused) {
            inputRef.current.focus();
        }
    }

    const handleSearchClear = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        setSearchValue('');
        if (inputRef.current) {
            inputRef.current.value = '';
            inputRef.current.blur();
        }
    }

    // This is the search functionality.
    const filterMenuItems = (menuItems: SideNavigationMenuItemType[]): SideNavigationMenuItemType[] => {
        return menuItems.filter((menuItem) => {
            // Check if the label of the menu item contains the search value
            if (menuItem.label.toLowerCase().includes(searchValue.toLowerCase())) {
                // If it matches, we also want to include its children
                const filteredChildren = filterMenuItems(menuItem.children || []);
                return [
                    {
                        ...menuItem,
                        children: filteredChildren,
                    },
                ];
            }

            // If the menu item doesn't match, check its children
            if (menuItem.children) {
                const filteredChildren = filterMenuItems(menuItem.children);
                if (filteredChildren.length > 0) {
                    return [
                        {
                            ...menuItem,
                            children: filteredChildren,
                        },
                    ];
                }
            }

            return null;
        }).filter((item) => item !== null) as SideNavigationMenuItemType[];
    }

    // When the debounced value changes, call onChange with the new value
    useEffect(() => {
        if (!isInitialRender) {
            setIsInitialRender(false);
            const filteredPrimaryMenuItems = filterMenuItems(primaryMenuItems);
            const filteredSecondaryMenuItems = filterMenuItems(secondaryMenuItems);
            onChange([filteredPrimaryMenuItems, filteredSecondaryMenuItems]);
        } else {
            setIsInitialRender(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchValue]);

    const propsToSpread: SideNavigationSearchBarProps = {
        placeholder,
        searchValue,
        setSearchValue,
        handleSearchClear,
        handleFocusingSearch,
        isFocused,
        setIsFocused
    }

    const collapsedOrExpandedDisplay = isCollapsed ?
        <SideNavigationSearchCollapsed {...propsToSpread} ref={inputRef}/> :
        <SideNavigationSearchBar {...propsToSpread} ref={inputRef}/>

    return <>{collapsedOrExpandedDisplay}</>;
}